import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Plus, Trash2, Send, Upload, Users, Megaphone, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CONTACT_STATUS = { new: "Novo", qualified: "Qualificado", contacted: "Contatado", opportunity: "Oportunidade", won: "Ganho", lost: "Perdido" };
const CONTACT_COLOR = { new: "#71717A", qualified: "#8B5CF6", contacted: "#3B82F6", opportunity: "#D4AF37", won: "#10B981", lost: "#EF4444" };

const emptyContact = { name: "", email: "", phone: "", company: "", role: "", tags: [], source: "", status: "new", notes: "" };

export default function Prospecting() {
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [contactForm, setContactForm] = useState(emptyContact);
  const [contactOpen, setContactOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");

  const load = useCallback(async () => {
    const c = await api.get("/contacts"); setContacts(c.data);
    const k = await api.get("/campaigns"); setCampaigns(k.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveContact = async () => {
    if (!contactForm.name) return toast.error("Nome obrigatório");
    await api.post("/contacts", contactForm);
    toast.success("Contato adicionado");
    setContactOpen(false); setContactForm(emptyContact); load();
  };

  const removeContact = async (id) => { await api.delete(`/contacts/${id}`); load(); };

  const importCsv = async () => {
    const lines = csvText.trim().split(/\r?\n/);
    if (lines.length === 0) return toast.error("Vazio");
    // Expected header: name,email,phone,company,role
    const header = lines[0].toLowerCase().split(/[,;]/).map((h) => h.trim());
    const idx = (k) => header.indexOf(k);
    const items = lines.slice(1).map((l) => {
      const cols = l.split(/[,;]/).map((c) => c.trim().replace(/^["']|["']$/g, ""));
      return {
        name: cols[idx("name")] || cols[idx("nome")] || cols[0] || "",
        email: cols[idx("email")] || "",
        phone: cols[idx("phone")] || cols[idx("telefone")] || "",
        company: cols[idx("company")] || cols[idx("empresa")] || "",
        role: cols[idx("role")] || cols[idx("cargo")] || "",
        tags: [], source: "csv", status: "new", notes: "",
      };
    }).filter((c) => c.name);
    if (items.length === 0) return toast.error("Nenhum contato válido");
    await api.post("/contacts/bulk", items);
    toast.success(`${items.length} contato(s) importado(s)`);
    setCsvOpen(false); setCsvText(""); load();
  };

  const createCampaign = async () => {
    const name = prompt("Nome da campanha:");
    if (!name) return;
    const r = await api.post("/campaigns", { name, channel: "both", subject: "", body: "", recipient_ids: [] });
    window.location.href = `/prospecting/campaigns/${r.data.campaign_id}`;
  };

  return (
    <div className="p-10 max-w-[1400px]">
      <div className="mb-8">
        <p className="overline">Prospecção B2B</p>
        <h1 className="font-heading text-4xl mt-2">Contatos & Campanhas</h1>
        <p className="text-sm text-muted-foreground mt-2">Construa sua base de leads e envie mensagens personalizadas em escala.</p>
      </div>

      <Tabs defaultValue="contacts">
        <TabsList className="mb-6 bg-white border border-border">
          <TabsTrigger value="contacts" data-testid="tab-contacts"><Users size={13} className="mr-1" /> Contatos ({contacts.length})</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns"><Megaphone size={13} className="mr-1" /> Campanhas ({campaigns.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts">
          <div className="flex justify-end gap-2 mb-3">
            <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-csv-btn"><Upload size={14} className="mr-1" /> Importar CSV</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>Importar contatos (CSV)</DialogTitle></DialogHeader>
                <p className="text-xs text-muted-foreground mb-2">Cole abaixo. Primeira linha: cabeçalho <code>name,email,phone,company,role</code> (ou nome,email,telefone,empresa,cargo).</p>
                <Textarea rows={10} value={csvText} onChange={(e) => setCsvText(e.target.value)} className="font-mono text-xs" data-testid="csv-text" placeholder="name,email,phone,company,role&#10;Ana Silva,ana@x.com,5511999999999,Acme,Diretora RH" />
                <DialogFooter><Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={importCsv} data-testid="csv-import-btn">Importar</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={contactOpen} onOpenChange={setContactOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#C07C88] hover:bg-[#A86571]" data-testid="new-contact-btn"><Plus size={14} className="mr-1" /> Novo contato</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Novo contato</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Nome</Label><Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} data-testid="contact-name" /></div>
                  <div><Label>E-mail</Label><Input value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} data-testid="contact-email" /></div>
                  <div><Label>Telefone/WhatsApp</Label><Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="5511999999999" data-testid="contact-phone" /></div>
                  <div><Label>Empresa</Label><Input value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} data-testid="contact-company" /></div>
                  <div><Label>Cargo</Label><Input value={contactForm.role} onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })} data-testid="contact-role" /></div>
                  <div className="col-span-2"><Label>Notas</Label><Textarea rows={2} value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} /></div>
                </div>
                <DialogFooter><Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={saveContact} data-testid="save-contact-btn">Salvar</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="border-border shadow-none">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-4">Nome</th><th className="p-4">Empresa</th><th className="p-4">E-mail</th><th className="p-4">WhatsApp</th><th className="p-4">Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.contact_id} className="border-t border-border" data-testid={`contact-row-${c.contact_id}`}>
                    <td className="p-4"><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.role}</p></td>
                    <td className="p-4 text-xs">{c.company}</td>
                    <td className="p-4 text-xs">{c.email}</td>
                    <td className="p-4 text-xs">{c.phone}</td>
                    <td className="p-4"><span className="inline-flex items-center text-xs"><span className="status-dot" style={{ background: CONTACT_COLOR[c.status] }} />{CONTACT_STATUS[c.status]}</span></td>
                    <td className="p-4 text-right"><button onClick={() => removeContact(c.contact_id)} className="p-1.5 text-muted-foreground hover:text-red-500"><Trash2 size={13} /></button></td>
                  </tr>
                ))}
                {contacts.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-sm text-muted-foreground">Sem contatos ainda. Adicione manualmente ou importe CSV.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns">
          <div className="flex justify-end mb-3">
            <Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={createCampaign} data-testid="new-campaign-btn"><Plus size={14} className="mr-1" /> Nova campanha</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {campaigns.map((c) => (
              <Link to={`/prospecting/campaigns/${c.campaign_id}`} key={c.campaign_id} data-testid={`campaign-${c.campaign_id}`}>
                <Card className="p-5 border-border shadow-none hover:-translate-y-0.5 hover:shadow-sm transition-all bg-white">
                  <div className="flex items-start justify-between">
                    <p className="font-heading text-lg">{c.name}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ background: c.status === "sent" ? "#10B98120" : "#71717A20", color: c.status === "sent" ? "#059669" : "#52525B" }}>{c.status}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Canal: {c.channel} · {c.recipient_ids?.length || 0} destinatários</p>
                  {c.sent_at && <p className="text-[10px] text-muted-foreground mt-1">Enviada em {c.sent_at.slice(0,10)} · {c.sent_count} envios</p>}
                </Card>
              </Link>
            ))}
            {campaigns.length === 0 && <p className="text-xs text-muted-foreground text-center py-6 md:col-span-2">Nenhuma campanha ainda</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
